/**
 * Typed product-analytics event catalog (single source of truth for event names + params).
 *
 * Every client-side analytics event flows through a `track*` helper here rather than calling
 * `getAnalyticsReporter().logEvent(...)` inline, so names/params stay consistent and greppable and
 * the shared signal stores (web + React Native) emit identical events. GA4's recommended event
 * names (`sign_up`, `login`, `begin_checkout`, `add_payment_info`) are used where they exist so
 * GA4's built-in reports light up; the rest are custom snake_case names.
 *
 * The authoritative `purchase` event is emitted **server-side** (GA4 Measurement Protocol) on a
 * confirmed booking — never from here — to avoid double counting and under-reporting revenue.
 *
 * PII rule (GA4 ToS + luxury-client sensitivity): never pass names, emails, phone numbers, or free
 * text a traveler typed. Ids (trip/chat/item) and low-cardinality enums only.
 */
import type { AnalyticsParams } from './analytics.js';
import type { PreferenceCategory } from '@elara/domain/models/schemas/shared-schemas';

import { getAnalyticsReporter } from './analytics.js';

/** Canonical GA4 event names. Keyed by a semantic slug used across the codebase. */
export const ANALYTICS_EVENTS = {
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  APPLICATION_STARTED: 'application_started',
  MEMBERSHIP_LEARN_MORE: 'membership_learn_more',
  SHARED_ITINERARY_PRINTED: 'shared_itinerary_printed',
  ITINERARY_PRINTED: 'itinerary_printed',
  PHONE_VERIFICATION_STARTED: 'phone_verification_started',
  PHONE_VERIFICATION_COMPLETED: 'phone_verification_completed',
  ELARA_CONTACT_CARD_OPENED: 'elara_contact_card_opened',
  TRIP_CREATED: 'trip_created',
  TRIP_DELETED: 'trip_deleted',
  TRIP_PROMPT_USED: 'trip_prompt_used',
  MESSAGE_SENT: 'message_sent',
  RESPONSE_STOPPED: 'response_stopped',
  SHORTLIST_ITEM_ADDED: 'shortlist_item_added',
  SHORTLIST_ITEM_DELETED: 'shortlist_item_deleted',
  ITINERARY_ITEM_ADDED: 'itinerary_item_added',
  ITINERARY_ITEM_DELETED: 'itinerary_item_deleted',
  ROOM_COUNT_INCREASED: 'room_count_increased',
  ROOM_COUNT_DECREASED: 'room_count_decreased',
  BEGIN_CHECKOUT: 'begin_checkout',
  ADD_PAYMENT_INFO: 'add_payment_info',
  REFERRAL_SHARED: 'referral_shared',
  AVATAR_UPDATED: 'avatar_updated',
  ACCOUNT_DELETION_REQUESTED: 'account_deletion_requested',
  PREFERENCE_PROPOSAL_DECIDED: 'preference_proposal_decided',
  PREFERENCE_ADDED: 'preference_added',
  PREFERENCE_REMOVED: 'preference_removed',
  PREFERENCE_UPDATED: 'preference_updated',
  COMPANION_SAVED: 'companion_saved',
  COMPANION_REMOVED: 'companion_removed',
  TRIP_SHARE_ENABLED: 'trip_share_enabled',
  TRIP_SHARE_DISABLED: 'trip_share_disabled',
  SHARE: 'share',
  SELECT_CONTENT: 'select_content',
  SHARED_ITINERARY_VIEWED: 'shared_itinerary_viewed',
  ROOM_SEARCH: 'room_search',
  ROOM_SEARCH_RESULTS_VIEWED: 'room_search_results_viewed',
  NOTIFICATION_PROMPT_ANSWERED: 'notification_prompt_answered',
  CHAT_MESSAGE_NOTIFICATION_OPENED: 'chat_message_notification_opened',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** The sign-in/sign-up method surfaced to GA4 (its recommended `method` param). */
export type AuthMethod = 'password' | 'google' | 'apple' | 'phone' | 'link';

/** Low-level escape hatch: log any catalog event with arbitrary params. Prefer the typed helpers. */
export function trackEvent(name: AnalyticsEventName, params?: AnalyticsParams): void {
  getAnalyticsReporter().logEvent(name, params);
}

export function trackSignUp(method: AuthMethod): void {
  trackEvent(ANALYTICS_EVENTS.SIGN_UP, { method });
}

export function trackLogin(method: AuthMethod): void {
  trackEvent(ANALYTICS_EVENTS.LOGIN, { method });
}

/**
 * A prospective member leaves an Elara surface for the public application form. `placement` says
 * which call-to-action on that surface was used (e.g. the shared itinerary's top banner vs footer).
 */
export function trackApplicationStarted(params: {
  source: 'mobile' | 'web' | 'shared_itinerary';
  placement?: 'banner' | 'footer';
}): void {
  trackEvent(ANALYTICS_EVENTS.APPLICATION_STARTED, { source: params.source, placement: params.placement });
}

/** A recipient prints the public shared itinerary from its Print button (no token or trip id). */
export function trackSharedItineraryPrinted(): void {
  trackEvent(ANALYTICS_EVENTS.SHARED_ITINERARY_PRINTED);
}

/** A prospective member follows a "learn more" link from an Elara surface to the marketing site. */
export function trackMembershipLearnMore(params: { source: 'shared_itinerary'; placement: 'banner' | 'footer' }): void {
  trackEvent(ANALYTICS_EVENTS.MEMBERSHIP_LEARN_MORE, { source: params.source, placement: params.placement });
}

export function trackPhoneVerificationStarted(): void {
  trackEvent(ANALYTICS_EVENTS.PHONE_VERIFICATION_STARTED);
}

export function trackPhoneVerificationCompleted(): void {
  trackEvent(ANALYTICS_EVENTS.PHONE_VERIFICATION_COMPLETED);
}

/**
 * The member opened Elara's contact card from "Add Elara to Contacts" (ELARA-1178). Opened, not saved:
 * the phone's own contact screen takes it from there and reports nothing back.
 */
export function trackElaraContactCardOpened(): void {
  trackEvent(ANALYTICS_EVENTS.ELARA_CONTACT_CARD_OPENED);
}

export function trackTripCreated(params?: { tripId?: string; source?: string }): void {
  trackEvent(ANALYTICS_EVENTS.TRIP_CREATED, { trip_id: params?.tripId, source: params?.source });
}

export function trackTripDeleted(params?: { tripId?: string }): void {
  trackEvent(ANALYTICS_EVENTS.TRIP_DELETED, { trip_id: params?.tripId });
}

/**
 * A suggested prompt on the trip overview was taken — tapped, and carried into the composer.
 *
 * "Used" rather than "sent": the prompt fills the composer and the guest finishes and sends it
 * themselves, so a send is a separate act (and its own `message_sent`). The funnel this answers is
 * unchanged — does surfacing a suggestion actually get a trip moving — but the name has to describe
 * what the tap does, or the report reads as a count of messages that were never sent.
 *
 * `prompt_kind` and `trip_stage` are both closed enums — the message text is deliberately NOT sent,
 * since prompts include destination names ("Find a hotel in Kyoto"), which would put free text, and
 * eventually a guest's travel plans, into an analytics event.
 */
export function trackTripPromptUsed(params: {
  tripId?: string;
  /** A `TripPromptKind` — the way into planning, not the wording. */
  promptKind: string;
  /** The `TripStage` the overview was showing when it was tapped. */
  tripStage: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.TRIP_PROMPT_USED, {
    trip_id: params.tripId,
    prompt_kind: params.promptKind,
    trip_stage: params.tripStage,
  });
}

export function trackMessageSent(params?: { tripId?: string; chatId?: string; hasAttachments?: boolean }): void {
  trackEvent(ANALYTICS_EVENTS.MESSAGE_SENT, {
    trip_id: params?.tripId,
    chat_id: params?.chatId,
    has_attachments: params?.hasAttachments,
  });
}

/**
 * Fired when a user interrupts an in-progress concierge reply (the Stop button). `had_content`
 * distinguishes stopping before vs. after any text streamed. Ids only — no PII.
 */
export function trackResponseStopped(params?: { chatId?: string; tripId?: string; hadContent?: boolean }): void {
  trackEvent(ANALYTICS_EVENTS.RESPONSE_STOPPED, {
    chat_id: params?.chatId,
    trip_id: params?.tripId,
    had_content: params?.hadContent,
  });
}

export function trackShortlistItemAdded(params?: { tripId?: string; itemType?: string }): void {
  trackEvent(ANALYTICS_EVENTS.SHORTLIST_ITEM_ADDED, { trip_id: params?.tripId, item_type: params?.itemType });
}

export function trackShortlistItemDeleted(params?: { tripId?: string; itemType?: string }): void {
  trackEvent(ANALYTICS_EVENTS.SHORTLIST_ITEM_DELETED, { trip_id: params?.tripId, item_type: params?.itemType });
}

export function trackItineraryItemAdded(params?: { tripId?: string; itemType?: string }): void {
  trackEvent(ANALYTICS_EVENTS.ITINERARY_ITEM_ADDED, { trip_id: params?.tripId, item_type: params?.itemType });
}

export function trackItineraryItemDeleted(params?: { tripId?: string; itemType?: string }): void {
  trackEvent(ANALYTICS_EVENTS.ITINERARY_ITEM_DELETED, { trip_id: params?.tripId, item_type: params?.itemType });
}

/**
 * The traveler stepped the room count of a rate up or down on the room count picker. `room_count` is the
 * number of rooms added (not booked) afterwards, so a funnel can see how far travelers take multi-room stays.
 */
export function trackRoomCountChanged(params: {
  tripId: string;
  direction: 'increase' | 'decrease';
  roomCount: number;
}): void {
  trackEvent(
    params.direction === 'increase' ? ANALYTICS_EVENTS.ROOM_COUNT_INCREASED : ANALYTICS_EVENTS.ROOM_COUNT_DECREASED,
    { trip_id: params.tripId, room_count: params.roomCount },
  );
}

/**
 * GA4 recommended `begin_checkout`. `value`/`currency` are optional — often unknown at checkout
 * entry. Line-item detail is intentionally omitted here; the authoritative, itemised revenue event
 * (`purchase`) is emitted server-side on a confirmed booking.
 */
export function trackBeginCheckout(params: {
  /** `membership` is the annual subscription at signup; the others are trips booked in-app. */
  itemCategory: 'hotel' | 'flight' | 'membership';
  tripId?: string;
  value?: number;
  currency?: string;
}): void {
  trackEvent(ANALYTICS_EVENTS.BEGIN_CHECKOUT, {
    item_category: params.itemCategory,
    trip_id: params.tripId,
    value: params.value,
    currency: params.currency,
  });
}

export function trackAddPaymentInfo(params?: { paymentType?: string }): void {
  trackEvent(ANALYTICS_EVENTS.ADD_PAYMENT_INFO, { payment_type: params?.paymentType });
}

export function trackReferralShared(params?: { channel?: string }): void {
  trackEvent(ANALYTICS_EVENTS.REFERRAL_SHARED, { channel: params?.channel });
}

/** A user set a custom profile photo (uploaded on web or mobile). No PII — the event has no params. */
export function trackAvatarUpdated(): void {
  trackEvent(ANALYTICS_EVENTS.AVATAR_UPDATED);
}

/** Fired when a member submits an in-app account-deletion request. No params — the pseudonymous UID
 * comes from GA4's `user_id`; per the PII rule we never attach the member's email or name. */
export function trackAccountDeletionRequested(): void {
  trackEvent(ANALYTICS_EVENTS.ACCOUNT_DELETION_REQUESTED);
}

export function trackPreferenceProposalDecided(params: { category: PreferenceCategory; decision: 'yes' | 'no' }): void {
  trackEvent(ANALYTICS_EVENTS.PREFERENCE_PROPOSAL_DECIDED, params);
}

/** A preference typed in settings (not one Elara proposed). `for_companion` separates family entries. */
export function trackPreferenceAdded(params: { category: PreferenceCategory; for_companion: boolean }): void {
  trackEvent(ANALYTICS_EVENTS.PREFERENCE_ADDED, params);
}

/** A saved preference removed in settings. `source` shows whether it was one Elara remembered. */
export function trackPreferenceRemoved(params: { category: PreferenceCategory; source: 'user' | 'elara' }): void {
  trackEvent(ANALYTICS_EVENTS.PREFERENCE_REMOVED, params);
}

/** A saved preference reworded in settings. */
export function trackPreferenceUpdated(params: { category: PreferenceCategory; for_companion: boolean }): void {
  trackEvent(ANALYTICS_EVENTS.PREFERENCE_UPDATED, params);
}

/** A travel companion saved in settings; `is_new` separates adding someone from editing them. */
export function trackCompanionSaved(params: { is_new: boolean }): void {
  trackEvent(ANALYTICS_EVENTS.COMPANION_SAVED, params);
}

/** A travel companion removed in settings. */
export function trackCompanionRemoved(): void {
  trackEvent(ANALYTICS_EVENTS.COMPANION_REMOVED);
}

/** The owner turned on a trip's public itinerary link. */
export function trackTripShareEnabled(params: { tripId: string }): void {
  trackEvent(ANALYTICS_EVENTS.TRIP_SHARE_ENABLED, { trip_id: params.tripId });
}

/** The owner printed their itinerary from the itinerary page's Print button. */
export function trackItineraryPrinted(params: { tripId: string }): void {
  trackEvent(ANALYTICS_EVENTS.ITINERARY_PRINTED, { trip_id: params.tripId });
}

/** The owner revoked a trip's public itinerary link. */
export function trackTripShareDisabled(params: { tripId: string }): void {
  trackEvent(ANALYTICS_EVENTS.TRIP_SHARE_DISABLED, { trip_id: params.tripId });
}

/** How an owner passed on a share link: copied it, or handed it to the OS share sheet. */
export type ShareMethod = 'copy_link' | 'native_share';

/** GA4 recommended `share` event, fired when the owner copies or natively shares an itinerary link. */
export function trackItineraryShared(params: { tripId: string; method: ShareMethod }): void {
  trackEvent(ANALYTICS_EVENTS.SHARE, { method: params.method, content_type: 'itinerary', item_id: params.tripId });
}

/** GA4 recommended `select_content` event, fired when a traveler opens a story's article. */
export function trackStoryOpened(params: { storyId: string }): void {
  trackEvent(ANALYTICS_EVENTS.SELECT_CONTENT, { content_type: 'story', item_id: params.storyId });
}

/**
 * A recipient opened a shared itinerary. Deliberately carries no trip id or token (the viewer is not
 * the owner, and the token is a credential) — only low-cardinality shape of what they saw.
 */
export function trackSharedItineraryViewed(params: {
  tripStatus: 'upcoming' | 'in_progress' | 'past' | 'undated';
  dayCount: number;
  isSignedIn: boolean;
}): void {
  trackEvent(ANALYTICS_EVENTS.SHARED_ITINERARY_VIEWED, {
    trip_status: params.tripStatus,
    day_count: params.dayCount,
    is_signed_in: params.isSignedIn,
  });
}

/**
 * A traveller ran a room search themselves from the hotel guide (ELARA-1092) — the self-serve twin of
 * asking the concierge. `mode` separates a new search from re-pricing a saved one. Fired once the
 * search returns rooms; party size is counts only, never names or ages.
 */
export function trackRoomSearch(params: {
  tripId: string;
  hotelId: string;
  nights: number;
  adults: number;
  children: number;
  mode: 'search' | 'refresh';
}): void {
  trackEvent(ANALYTICS_EVENTS.ROOM_SEARCH, {
    trip_id: params.tripId,
    hotel_id: params.hotelId,
    nights: params.nights,
    adults: params.adults,
    children: params.children,
    mode: params.mode,
  });
}

/**
 * A traveller opened the full results of a room search in the hotel guide. `entry` says from where:
 * `chat_view_all` is the "View all rooms" button under the concierge's room cards, which is how the
 * business sees whether chat recommendations send people on to browse.
 */
export function trackRoomSearchResultsViewed(params: {
  tripId: string;
  hotelId: string;
  entry: 'chat_view_all';
}): void {
  trackEvent(ANALYTICS_EVENTS.ROOM_SEARCH_RESULTS_VIEWED, {
    trip_id: params.tripId,
    hotel_id: params.hotelId,
    entry: params.entry,
  });
}

/**
 * How a guest answered an in-app offer to turn on notifications. `surface` is where it was offered
 * (`chat_banner`: above the composer while a reply takes a while). `outcome` is the browser's answer
 * (`granted`/`denied`/`default` when they closed the browser prompt), `dismissed` for "Not now", or
 * `open_settings` when notifications were already blocked and they went to fix it.
 */
export function trackNotificationPromptAnswered(params: {
  surface: 'chat_banner';
  outcome: 'granted' | 'denied' | 'default' | 'dismissed' | 'open_settings';
}): void {
  trackEvent(ANALYTICS_EVENTS.NOTIFICATION_PROMPT_ANSWERED, params);
}

/** A guest tapped a new-chat-message notification and landed on that message: the re-engagement it exists for. */
export function trackChatMessageNotificationOpened(params: { tripId: string; chatId?: string }): void {
  trackEvent(ANALYTICS_EVENTS.CHAT_MESSAGE_NOTIFICATION_OPENED, {
    trip_id: params.tripId,
    ...(params.chatId ? { chat_id: params.chatId } : {}),
  });
}
